import {
  type PractitionerCreateRequest,
  PractitionerCreateSchema,
} from '@quromedical/models';
import { Row } from 'components/base';
import {
  Alert,
  Button,
  Skeleton,
  SkeletonProvider,
  Snackbar,
  Spacer,
  Text,
  Visible,
} from 'design-system';
import { MARGIN } from 'design-system/theme';
import { router } from 'expo-router';
import { logger } from 'helpers';
import { useFiniteState } from 'hooks/useFiniteState';
import { useObjectState } from 'hooks/useObjectState';
import { useRevalidation } from 'hooks/useRevalidation';
import {
  getPlaceAddressPartialFetcher,
  googleSearchAddressFetcher,
} from 'integration';
import { PractitionerApi } from 'integration/aggregate';
import type React from 'react';
import { useCallback } from 'react';
import { View } from 'react-native';
import { PersonContactFormCard, PersonGeneralFormCard } from 'screens/person';
import { strings } from 'strings';
import { PractitionerCreateInitialGroupForm } from './PractitionerCreateInitialGroupForm';
import { PractitionerExternalForm } from './PractitionerExternalForm';
import { PractitionerQualificationForm } from './PractitionerQualificationForm';
import { PractitionerSettingsForm } from './PractitionerSettingsForm';

const api = new PractitionerApi();

type FormState =
  | 'interactive'
  | 'submitting'
  | 'validation-error'
  | 'submission-error'
  | 'loading'
  | 'complete';

export const PractitionerCreateForm: React.FC = () => {
  const revalidate = useRevalidation('Practitioner');

  const formState = useFiniteState<FormState>('interactive');
  const formData = useObjectState<Partial<PractitionerCreateRequest>>({});
  const showErrors = formState.is('validation-error');

  const handleSubmit = useCallback(async () => {
    const defined = formData.getDefined([
      'contact',
      'general',
      'qualification',
      'initialGroup',
    ]);
    const parsedData = PractitionerCreateSchema.safeParse(defined);

    if (!parsedData.success || !defined) {
      formState.set('validation-error');
      return;
    }
    formState.set('loading');

    try {
      const result = await api.createPractitioner(defined);

      formState.set('complete');
      router.push({ pathname: `/admin/practitioner/${result.id}` });
    } catch (err) {
      logger.error(err);
      formState.set('submission-error');
    } finally {
      revalidate().catch(logger.error);
    }
  }, [formData, formState, revalidate]);

  return (
    <>
      <Snackbar
        isOpen={formState.is('submission-error')}
        onClose={formState.next('interactive')}
      >
        <Alert
          backgroundColor="status-critical"
          textColor="white"
          onAction={formState.next('interactive')}
          title={strings.ErrorCardTitle}
          body={strings.GenericErrorMessage}
          actionIcon="close"
        />
      </Snackbar>
      <SkeletonProvider loading={formState.is('loading')}>
        <Skeleton>
          <PersonGeneralFormCard
            onValidChange={formData.set('general')}
            showErrors={showErrors}
            minimalData={true}
          />
        </Skeleton>
        <Spacer height={MARGIN.s} />
        <Skeleton>
          <PersonContactFormCard
            onValidChange={formData.set('contact')}
            showErrors={showErrors}
            addressFetcher={googleSearchAddressFetcher}
            getAddressPartial={getPlaceAddressPartialFetcher}
          />
        </Skeleton>
        <Spacer height={MARGIN.s} />
        <Skeleton>
          <PractitionerQualificationForm
            onValidChange={formData.set('qualification')}
            showErrors={showErrors}
          />
        </Skeleton>
        <Spacer height={MARGIN.s} />
        <Skeleton>
          <PractitionerSettingsForm
            onValidChange={formData.set('settings')}
            showErrors={showErrors}
          />
        </Skeleton>
        <Spacer height={MARGIN.s} />
        <Skeleton>
          <PractitionerExternalForm
            onValidChange={formData.set('external')}
            showErrors={showErrors}
          />
        </Skeleton>
        <Spacer height={MARGIN.s} />
        <Skeleton>
          <PractitionerCreateInitialGroupForm
            onValidChange={formData.set('initialGroup')}
            showErrors={showErrors}
          />
        </Skeleton>
        <Spacer height={MARGIN.s} />
        <Skeleton>
          <Row alignItems="flex-end" justifyContent="space-between">
            <Visible if={formState.is('submission-error')}>
              <View>
                <Text>{strings.LabelPractitionerError}</Text>
              </View>
            </Visible>
          </Row>
          <Row justifyContent="flex-end">
            <Button
              onPress={handleSubmit}
              text={strings.ButtonTextSubmitPractitioner}
            />
          </Row>
        </Skeleton>
      </SkeletonProvider>
    </>
  );
};
